/**
 * contractDetailApi — RTK Query slice cho màn "Contract Detail" (Procurement Control).
 *
 * Access `frmProcurementMainSubFormDetailsNPE/…DetailsP`: header 1 hợp đồng (2 picker)
 * + Tab1 "Pay Info" (3 datasheet GoodsAlc/Paydata/Invoice), NPE thêm Advance/Fixation.
 * Web truyền `contractNo` (stateless — không temp table).
 *
 *   GET /purchase/contract-detail/contracts
 *   GET /purchase/contract-detail/header?variant=p|npe&contractNo=...
 *   GET /purchase/contract-detail/goods-alc?contractNo=...
 *   GET /purchase/contract-detail/paydata?contractNo=...
 *   GET /purchase/contract-detail/invoice?contractNo=...
 *   GET /purchase/contract-detail/advance?contractNo=...
 *   GET /purchase/contract-detail/fixation?contractNo=...
 *
 * Đọc snake_case raw (như ProcurementDatasheet) → field giữ nguyên snake_case.
 */
import { baseApi } from './baseApi'
import type { PageResponse } from '@/types/api'

export interface ContractPickP {
  contract_no: string
  npe_ref: string | null
  contract_date: string | null
  contract_type: string | null
  latest_delivery: string | null
  supplier_name: string | null
}

export interface ContractPickNPE {
  contract_no: string
  contract_date: string | null
  supplier_name: string | null
  latest_delivery: string | null
}

export interface ContractPicks {
  p: ContractPickP[]
  npe: ContractPickNPE[]
}

/** Header P — 1 row mv_local_p (Access ProcurementPayDataQryLocalP_pick). */
export interface HeaderP {
  contract_no: string | null
  npe_ref: string | null
  delivery_place: string | null
  supplier_name: string | null
  price_unit: string | null
  avg_futures_price_real: number | null
  quality_code: string | null
  certification: string | null
  contract_type: string | null
  outright_price: number | null
  ptbf_diffs: number | null
  avg_price2_real: number | null
  latest_delivery: string | null
  futures_month: string | null
  intermediary_text: string | null
  quantity_kg: number | null
  total_lot: number | null
  contract_shipper_intermediary: string | null
  contract_date: string | null
  lot_fixed: number | null
  shipper_name_short: string | null
  received: number | null
  kg_fixed: number | null
  fixed_fx: number | null
  to_be_received: number | null
  kg_to_fix: number | null
  price_diffs_2: number | null
  is_forced_close_int: number | null
  fix_level: number | null
  [key: string]: unknown
}

/** Header NPE — 1 row v_consignment_p (Access ProcurementPayDataQryNPE_pick). */
export interface HeaderNPE {
  contract_no: string | null
  certification: string | null
  contract_date: string | null
  delivery_time: string | null
  package_name: string | null
  cp_code: string | null
  quality_code: string | null
  interest_rate_note: string | null
  cp_name: string | null
  supplier_name: string | null
  quantity_kg: number | null
  paid_vnd: number | null
  delivery_place: string | null
  no_bag: number | null
  note: string | null
  stoploss_rate: number | null
  provisional_market_rate: number | null
  received: number | null
  advanced: number | null
  fixed: number | null
  to_be_receive: number | null
  to_be_advance: number | null
  to_be_fix: number | null
  [key: string]: unknown
}

export interface GoodsAlcRow {
  id: number | null
  contract_no: string | null
  m_date: string | null
  m_truck_no: string | null
  truck_id: string | null
  truck_status: string | null
  estimated_quantity: number | null
  allocating_quantity: number | null
  requested_qry: number | null
  requested_provi: number | null
  to_be_requested_provi: number | null
  requested_fix: number | null
  to_be_requested_fix: number | null
  to_warehouse: string | null
  receipt_no: string | null
  [key: string]: unknown
}

export interface InvoiceRow {
  invoice_id: number | null
  warehouse: string | null
  invoice_no: string | null
  invoice_date: string | null
  quantity: number | null
  price_vnd_kg: number | null
  total_payment: number | null
  total_amount: number | null
  vat_amount: number | null
  total_amount_after_tax: number | null
  [key: string]: unknown
}

export interface PaydataRow {
  paydata_p_id: number | null
  contract_no: string | null
  contract_show: string | null
  payment_type: string | null
  xntt_no: string | null
  truck_id: string | null
  bank: string | null
  quantity: number | null
  payment_date: string | null
  requested_usd: number | null
  requested_fx: number | null
  requested_vnd: number | null
  paid_usd: number | null
  fx: number | null
  paid_vnd: number | null
  reconcile: number | null
  invoice_no: string | null
  stoploss: number | null
  advanced: number | null
  vat_amount_usd: number | null
  vat_amount_vnd: number | null
  note: string | null
  [key: string]: unknown
}

export interface AdvanceRow {
  paydata_p_id: number | null
  payment_date: string | null
  contract_no: string | null
  xntt_no: string | null
  payment_type: string | null
  bank: string | null
  quantity: number | null
  price: number | null
  requested_usd: number | null
  requested_fx: number | null
  requested_vnd: number | null
  vat_amount_vnd: number | null
  paid_usd: number | null
  fx: number | null
  paid_vnd: number | null
  reconcile: number | null
  interest_rate: number | null
  fixation_deadline: string | null
  invoice_no: string | null
  invoice_date: string | null
  invoice_amount: number | null
  warehouse: string | null
  note: string | null
  supplier_id: string | null
  bank_account: string | null
  account_name: string | null
  bank_name: string | null
  contract_date: string | null
  quality_code: string | null
  date_update: string | null
  person_update: string | null
  must_fix: number | null
  must_fix_txt: string | null
  [key: string]: unknown
}

export interface FixationRow {
  cs_fixation_id: number | null
  fixation_date: string | null
  fixation_no: string | null
  contract_no: string | null
  cp_code: string | null
  name: string | null
  quality_code: string | null
  quantity_kg: number | null
  price: number | null
  p_contract_no: string | null
  price_after_interest: number | null
  to_be_paid: number | null
  total_amount: number | null
  allocated_amount: number | null
  allocated_interest: number | null
  allocated_quantity: number | null
  p_paid: number | null
  note: string | null
  person_update: string | null
  date_update: string | null
  [key: string]: unknown
}

// Tags để invalidate list sau khi Delete/Save (Access Requery sau ghi).
const taggedBase = baseApi.enhanceEndpoints({
  addTagTypes: [
    'CDPaydata',
    'CDInvoice',
    'CDGoodsAlc',
    'CDAdvance',
    'CDFixation',
  ],
})

export const contractDetailApi = taggedBase.injectEndpoints({
  endpoints: (b) => ({
    getContractPicks: b.query<ContractPicks, void>({
      query: () => ({ url: '/purchase/contract-detail/contracts' }),
      // ChooseContract dropdown — refetch qua Reload (ContractAll trong baseTags của ProcurementPage).
      providesTags: [{ type: 'ContractAll', id: 'LIST' }],
    }),
    getContractHeaderP: b.query<HeaderP, { contractNo: string }>({
      query: ({ contractNo }) => ({
        url: '/purchase/contract-detail/header',
        params: { variant: 'p', contractNo },
      }),
      providesTags: (_r, _e, arg) => [{ type: 'ContractAll', id: arg.contractNo }],
    }),
    getContractHeaderNPE: b.query<HeaderNPE, { contractNo: string }>({
      query: ({ contractNo }) => ({
        url: '/purchase/contract-detail/header',
        params: { variant: 'npe', contractNo },
      }),
      providesTags: (_r, _e, arg) => [{ type: 'ContractAll', id: arg.contractNo }],
    }),
    listContractGoodsAlc: b.query<PageResponse<GoodsAlcRow>, { contractNo: string }>({
      query: ({ contractNo }) => ({
        url: '/purchase/contract-detail/goods-alc',
        params: { contractNo },
      }),
      providesTags: ['CDGoodsAlc'],
    }),
    listContractInvoice: b.query<PageResponse<InvoiceRow>, { contractNo: string }>({
      query: ({ contractNo }) => ({
        url: '/purchase/contract-detail/invoice',
        params: { contractNo },
      }),
      providesTags: ['CDInvoice'],
    }),
    listContractPaydata: b.query<PageResponse<PaydataRow>, { contractNo: string }>({
      query: ({ contractNo }) => ({
        url: '/purchase/contract-detail/paydata',
        params: { contractNo },
      }),
      providesTags: ['CDPaydata'],
    }),
    listContractAdvance: b.query<PageResponse<AdvanceRow>, { contractNo: string }>({
      query: ({ contractNo }) => ({
        url: '/purchase/contract-detail/advance',
        params: { contractNo },
      }),
      providesTags: ['CDAdvance'],
    }),
    listContractFixation: b.query<PageResponse<FixationRow>, { contractNo: string }>({
      query: ({ contractNo }) => ({
        url: '/purchase/contract-detail/fixation',
        params: { contractNo },
      }),
      providesTags: ['CDFixation'],
    }),
    // Access Tab2 btnDelete — guard npe_allocation(advanced_no) BE-side.
    deleteContractAdvance: b.mutation<{ ok: boolean }, { paydataId: number }>({
      query: ({ paydataId }) => ({
        url: `/purchase/contract-detail/advance/${paydataId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CDAdvance', 'CDPaydata'],
    }),
    // Access Tab3 btnDelete — soft-delete 4 bảng BE-side (guards allocated/paid).
    deleteContractFixation: b.mutation<{ ok: boolean }, { fixationId: number }>({
      query: ({ fixationId }) => ({
        url: `/purchase/contract-detail/fixation/${fixationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CDFixation', 'CDPaydata', 'CDAdvance'],
    }),
  }),
})

export const {
  useGetContractPicksQuery,
  useGetContractHeaderPQuery,
  useGetContractHeaderNPEQuery,
  useListContractGoodsAlcQuery,
  useListContractInvoiceQuery,
  useListContractPaydataQuery,
  useListContractAdvanceQuery,
  useListContractFixationQuery,
  useDeleteContractAdvanceMutation,
  useDeleteContractFixationMutation,
} = contractDetailApi

/** Invalidate list con sau khi dialog ghi xong (Write data / New / Edit / Add Pay). */
export const invalidateContractDetailLists = () =>
  contractDetailApi.util.invalidateTags([
    'CDPaydata',
    'CDInvoice',
    'CDGoodsAlc',
    'CDAdvance',
    'CDFixation',
  ])
